import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Plus, X, MapPin, Zap, DollarSign, SquareStack, Clock, CheckSquare, Building2, Trash2 } from 'lucide-react';
import { canEditTask } from '../lib/taskAccess';
import { isActiveTask, isClosedTask } from '../lib/taskStatus';
import { dueLabel, isOverdue } from '../lib/taskDue.mjs';
import { searchNeedle, matchesSearch } from '../lib/listSearch.mjs';
import { countWord } from '../lib/countWord.mjs';
import { placeText } from '../lib/placeText.mjs';
import ShipmentPanel from '../components/ShipmentPanel';

const WATCHLIST_STAGE_META = {
  watching:      { label: 'Watching',       color: 'bg-slate-400' },
  outreach_sent: { label: 'Outreach Sent',  color: 'bg-blue-500' },
  engaged:       { label: 'Engaged',        color: 'bg-indigo-500' },
  specifying:    { label: 'Specifying',     color: 'bg-amber-500' },
  won:           { label: 'Won',            color: 'bg-green-600' },
  lost:          { label: 'Lost',           color: 'bg-gray-400' },
};

const TIER_COLOR = { A: 'bg-red-100 text-red-700', B: 'bg-amber-100 text-amber-700', C: 'bg-gray-100 text-gray-600' };

const ROLE_META = {
  owner_developer:    { label: 'Owner / Developer',   color: 'bg-purple-100 text-purple-700' },
  general_contractor: { label: 'General Contractor',  color: 'bg-blue-100 text-blue-700' },
  architect:          { label: 'Architect',            color: 'bg-teal-100 text-teal-700' },
  design_firm:        { label: 'Design Firm',          color: 'bg-cyan-100 text-cyan-700' },
  engineer:           { label: 'Engineer',             color: 'bg-emerald-100 text-emerald-700' },
  subcontractor:      { label: 'Subcontractor',        color: 'bg-amber-100 text-amber-700' },
  other:              { label: 'Other',                color: 'bg-gray-100 text-gray-600' },
};

const ROLE_ORDER = ['owner_developer', 'general_contractor', 'architect', 'design_firm', 'engineer', 'subcontractor', 'other'];

function fmtSqft(n) {
  if (!n) return null;
  return n >= 1000000 ? `${(n / 1000000).toFixed(1)}M sqft` : `${(n / 1000).toFixed(0)}k sqft`;
}

// The shared helper sends an expired session to sign-in and puts the HTTP status on the error, as the page's reads do.
function apiPatch(path, body) {
  return api.patch(path, body);
}

// The shared helper sends an expired session to sign-in and puts the HTTP status on the error, as the page's reads do.
function apiDelete(path) {
  return api.delete(path);
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [tierFilter, setTierFilter] = useState('A');
  const [stageFilter, setStageFilter] = useState('');
  const [q, setQ] = useState('');
  const reqId = useRef(0);

  const load = useCallback(async () => {
    // Only the latest filter request may update the list.
    // Slower responses for earlier filters are ignored.
    const id = ++reqId.current;
    try {
      const params = new URLSearchParams();
      if (tierFilter) params.set('tier', tierFilter);
      if (stageFilter) params.set('stage', stageFilter);
      const r = await api.get(`/crm/projects?${params}`);
      if (id !== reqId.current) return;
      setProjects(r.projects || []);
      setError('');
    } catch (e) {
      if (id === reqId.current) {
        // A failed load empties the list so projects from the last filter never appear under the new one.
        setProjects([]);
        setError(e.message);
      }
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [tierFilter, stageFilter]);

  useEffect(() => { load(); }, [load]);

  const needle = searchNeedle(q);
  const filtered = projects.filter((p) => matchesSearch(needle, [p.project_name, p.owner_operator, p.region]));

  const stageCounts = filtered.reduce((acc, p) => {
    acc[p.watchlist_stage] = (acc[p.watchlist_stage] || 0) + 1;
    return acc;
  }, {});

  if (loading) return <div className="p-8 text-[var(--text-secondary)]">Loading watchlist…</div>;

  return (
    <div className="sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Project Watchlist</h1>
          {/* A failed load shows its error without an empty project count. */}
          {!error && (
            <p className="text-sm text-[var(--text-secondary)]">
              {filtered.length} {countWord(filtered.length, 'project')}{Object.entries(stageCounts).map(([s, n]) =>
                ` · ${WATCHLIST_STAGE_META[s]?.label || s} ${n}`).join('')}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-[var(--border)] overflow-hidden">
            {['', 'A', 'B', 'C'].map((t) => (
              <button key={t} onClick={() => setTierFilter(t)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${tierFilter === t ? 'bg-primary text-white' : 'bg-white dark:bg-slate-800 text-[var(--text-secondary)] hover:bg-gray-50'}`}>
                {t || 'All'}
              </button>
            ))}
          </div>
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
            className="rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800">
            <option value="">All stages</option>
            {Object.entries(WATCHLIST_STAGE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800 sm:w-auto" />
        </div>
      </div>

      {error && <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-[var(--text-secondary)] dark:bg-slate-800">
            <tr>
              <th className="px-3 py-2.5 sm:px-4">Project</th>
              <th className="hidden sm:table-cell px-4 py-2.5">Owner / Operator</th>
              <th className="hidden sm:table-cell px-4 py-2.5">Location</th>
              <th className="hidden sm:table-cell px-4 py-2.5">Size</th>
              <th className="hidden sm:table-cell px-4 py-2.5">Stage</th>
              <th className="px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} onClick={() => setOpenId(p.id)}
                className="cursor-pointer border-t border-[var(--border)] bg-white hover:bg-gray-50 dark:bg-slate-900 dark:hover:bg-slate-800">
                <td className="px-3 py-3 sm:px-4">
                  <div className="flex items-center gap-2">
                    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${TIER_COLOR[p.tier] || 'bg-gray-100 text-gray-600'}`}>
                      T{p.tier}
                    </span>
                    <span className="min-w-0 break-words font-medium text-[var(--text-primary)] sm:max-w-xs sm:truncate">{p.project_name}</span>
                    {p.company_count > 0 && (
                      <span className="shrink-0 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-600"
                        title={`${p.company_count} ${p.company_count === 1 ? 'company' : 'companies'} linked`}>
                        {p.company_count} co.
                      </span>
                    )}
                  </div>
                  {p.project_type && <p className="mt-0.5 text-xs text-[var(--text-secondary)] pl-8">{p.project_type}</p>}
                  <p className="mt-0.5 pl-8 text-xs text-[var(--text-secondary)] sm:hidden">{[p.owner_operator, placeText(p.city, p.region), fmtSqft(p.slab_sqft), p.pec_stage].filter(Boolean).join(' · ')}</p>
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-[var(--text-secondary)]">{p.owner_operator || '—'}</td>
                <td className="hidden sm:table-cell px-4 py-3 text-[var(--text-secondary)]">
                  {p.region ? <span className="flex items-center gap-1"><MapPin size={11} />{p.city ? `${p.city.split(',')[0]}, ` : ''}{p.region}</span> : '—'}
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-[var(--text-secondary)]">
                  {fmtSqft(p.slab_sqft) || '—'}
                  {p.tiv_usd_millions && <span className="block text-xs">${p.tiv_usd_millions}M TIV</span>}
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-xs text-[var(--text-secondary)]">{p.pec_stage || '—'}</td>
                <td className="px-3 py-3 sm:px-4">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-white ${WATCHLIST_STAGE_META[p.watchlist_stage]?.color || 'bg-gray-400'}`}>
                    {WATCHLIST_STAGE_META[p.watchlist_stage]?.label || p.watchlist_stage}
                  </span>
                </td>
              </tr>
            ))}
            {!filtered.length && !error && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-[var(--text-secondary)]">
                  No projects match — try changing the tier or stage filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openId && <ProjectDrawer projectId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function ProjectDrawer({ projectId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setData(await api.get(`/crm/projects/${projectId}`));
  }, [projectId]);
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [load]);

  // Keep the drawer visible so a failed read can explain what happened.
  if (!data) {
    if (!err) return null;
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
        <div className="flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between border-b border-[var(--border)] p-5">
            <p className="pr-4 text-sm text-red-700">{err}</p>
            <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"><X size={18} /></button>
          </div>
        </div>
      </div>
    );
  }
  const { project, tasks, companies } = data;

  async function update(patch) {
    setErr('');
    setSaving(true);
    try { await apiPatch(`/crm/projects/${project.id}`, patch); await load(); onChanged(); }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  async function completeTask(taskId) {
    setErr('');
    try {
      await apiPatch(`/crm/tasks/${taskId}`, { status: 'done' });
      await load();
    } catch (e) { setErr(e.message); }
  }

  async function removeCompany(junctionId) {
    if (!confirm('Remove this company from the project?')) return;
    setErr('');
    try {
      await apiDelete(`/crm/project-companies/${junctionId}`);
      await load();
      onChanged();
    } catch (e) { setErr(e.message); }
  }

  const openTasks = tasks.filter((t) => isActiveTask(t.status)); // open + in_progress + on_hold
  const doneTasks = tasks.filter((t) => isClosedTask(t.status)); // done + skipped

  // Group companies by role in display order
  const companiesByRole = ROLE_ORDER.reduce((acc, role) => {
    const rows = companies.filter((c) => c.role === role);
    if (rows.length) acc.push({ role, rows });
    return acc;
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between border-b border-[var(--border)] p-5">
          <div className="pr-4">
            <div className="flex items-center gap-2 mb-1">
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${TIER_COLOR[project.tier] || ''}`}>Tier {project.tier}</span>
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-white ${WATCHLIST_STAGE_META[project.watchlist_stage]?.color || 'bg-gray-400'}`}>
                {WATCHLIST_STAGE_META[project.watchlist_stage]?.label || project.watchlist_stage}
              </span>
            </div>
            <h2 className="text-base font-semibold text-[var(--text-primary)] leading-tight">{project.project_name}</h2>
            <p className="text-sm text-[var(--text-secondary)]">{project.owner_operator}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"><X size={18} /></button>
        </div>

        {err && <p className="border-b border-[var(--border)] px-5 py-2 text-sm text-red-700">{err}</p>}

        {/* Key metrics */}
        <div className="grid grid-cols-3 gap-3 border-b border-[var(--border)] p-4">
          <Metric icon={SquareStack} label="Slab" value={fmtSqft(project.slab_sqft) || '—'} />
          <Metric icon={DollarSign} label="TIV" value={project.tiv_usd_millions ? `$${project.tiv_usd_millions}M` : '—'} />
          <Metric icon={Zap} label="MW" value={project.mw ? `${project.mw} MW` : '—'} />
        </div>

        {/* Location + probability */}
        <div className="border-b border-[var(--border)] px-5 py-3 text-sm text-[var(--text-secondary)] space-y-1">
          {(project.city || project.region) && (
            <p className="flex items-center gap-2"><MapPin size={13} />{[project.city?.split(',')[0], project.region].filter(Boolean).join(', ')}</p>
          )}
          {project.pec_stage && <p>PEC Stage: <b className="text-[var(--text-primary)]">{project.pec_stage}</b> — {project.pec_activity}</p>}
          {project.probability && <p>Probability: <b className="text-[var(--text-primary)]">{project.probability}</b></p>}
        </div>

        {/* Pitch angle */}
        {project.pitch_angle && (
          <div className="border-b border-[var(--border)] p-5">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">Concria Pitch Angle</p>
            <p className="text-sm text-[var(--text-primary)] leading-relaxed">{project.pitch_angle}</p>
          </div>
        )}

        <ShipmentPanel projectId={project.id} />

        {/* Companies on this project */}
        <div className="border-b border-[var(--border)] p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              Companies
              {companies.length > 0 && <span className="ml-2 text-xs font-normal text-[var(--text-secondary)]">({companies.length})</span>}
            </h3>
            <button onClick={() => setShowAddCompany(true)}
              className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-2.5 py-1 text-xs font-medium text-[var(--text-secondary)] hover:bg-gray-50">
              <Plus size={12} /> Add company
            </button>
          </div>
          {companiesByRole.length === 0 && (
            <p className="text-sm text-[var(--text-secondary)]">No companies linked yet — add the GC, architect, or owner.</p>
          )}
          {companiesByRole.map(({ role, rows }) => (
            <div key={role} className="mb-3">
              <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide mb-1.5 ${ROLE_META[role]?.color || 'bg-gray-100 text-gray-600'}`}>
                {ROLE_META[role]?.label || role}
              </span>
              {rows.map((c) => (
                <div key={c.id} className="flex items-start justify-between rounded-lg border border-[var(--border)] px-3 py-2 mb-1.5 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium text-[var(--text-primary)] truncate">{c.account_name}</p>
                    {c.contact_name && (
                      <p className="text-xs text-[var(--text-secondary)]">
                        {c.contact_name}{c.contact_title && ` · ${c.contact_title}`}
                        {c.contact_email && (
                          <> · <a className="text-primary hover:underline" href={`mailto:${c.contact_email}`}>{c.contact_email}</a></>
                        )}
                      </p>
                    )}
                    {c.notes && <p className="text-xs text-[var(--text-secondary)] mt-0.5 italic">{c.notes}</p>}
                  </div>
                  <button onClick={() => removeCompany(c.id)} title="Remove"
                    className="ml-2 shrink-0 rounded p-1 text-gray-300 hover:bg-red-50 hover:text-red-500">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Key contact (legacy from import) */}
        {project.key_contact_name && (
          <div className="border-b border-[var(--border)] p-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">Key Contact (import)</p>
            <div className="text-sm space-y-0.5">
              <p className="font-medium text-[var(--text-primary)]">{project.key_contact_name}
                {project.key_contact_title && <span className="font-normal text-[var(--text-secondary)]"> · {project.key_contact_title}</span>}
              </p>
              {project.key_contact_email && (
                <p><a className="text-primary hover:underline" href={`mailto:${project.key_contact_email}`}>{project.key_contact_email}</a></p>
              )}
              {project.key_contact_phone && <p className="text-[var(--text-secondary)]">{project.key_contact_phone}</p>}
              {project.contact_source && <p className="text-xs text-[var(--text-secondary)]">Source: {project.contact_source}</p>}
            </div>
          </div>
        )}

        {/* Watchlist stage + owner */}
        <div className="grid grid-cols-2 gap-3 border-b border-[var(--border)] p-5">
          <label className="text-xs font-medium text-[var(--text-secondary)]">Watchlist status
            <select value={project.watchlist_stage} disabled={saving}
              onChange={(e) => update({ watchlist_stage: e.target.value })}
              className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800">
              {Object.entries(WATCHLIST_STAGE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[var(--text-secondary)]">Owner
            <input defaultValue={project.owner_email || ''} disabled={saving}
              onBlur={(e) => e.target.value !== (project.owner_email || '') && update({ owner_email: e.target.value || null })}
              className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800" />
          </label>
          <label className="col-span-2 text-xs font-medium text-[var(--text-secondary)]">Notes
            <textarea rows={2} defaultValue={project.notes || ''} disabled={saving}
              onBlur={(e) => e.target.value !== (project.notes || '') && update({ notes: e.target.value || null })}
              className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800" />
          </label>
        </div>

        {/* Tasks */}
        <div className="flex-1 p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Tasks ({openTasks.length} open)</h3>
            <button onClick={() => setShowAddTask(true)}
              className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-2.5 py-1 text-xs font-medium text-[var(--text-secondary)] hover:bg-gray-50">
              <Plus size={12} /> Add task
            </button>
          </div>
          <div className="space-y-2">
            {openTasks.map((t) => <TaskRow key={t.id} task={t} onComplete={() => completeTask(t.id)} />)}
            {!openTasks.length && (
              <p className="py-4 text-center text-sm text-[var(--text-secondary)]">No open tasks.</p>
            )}
            {doneTasks.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                  {doneTasks.length} completed
                </summary>
                <div className="mt-2 space-y-2">
                  {doneTasks.map((t) => <TaskRow key={t.id} task={t} done />)}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>

      {showAddTask && (
        <AddTaskModal
          projectId={project.id}
          onClose={() => setShowAddTask(false)}
          onSaved={() => { setShowAddTask(false); load(); }}
        />
      )}
      {showAddCompany && (
        <AddCompanyModal
          projectId={project.id}
          existingRoles={companies.map((c) => ({ account_id: c.account_id, role: c.role }))}
          onClose={() => setShowAddCompany(false)}
          onSaved={() => { setShowAddCompany(false); load(); onChanged(); }}
        />
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="flex flex-col items-center text-center">
      <Icon size={14} className="mb-1 text-[var(--text-secondary)]" />
      <p className="text-sm font-semibold text-[var(--text-primary)]">{value}</p>
      <p className="text-[11px] text-[var(--text-secondary)]">{label}</p>
    </div>
  );
}

function TaskRow({ task, onComplete, done }) {
  const overdue = isOverdue(task.due_at) && isActiveTask(task.status);
  return (
    <div className={`flex items-start gap-3 rounded-lg border p-3 ${done ? 'border-[var(--border)] opacity-50' : 'border-[var(--border)] bg-white dark:bg-slate-900'}`}>
      {!done && (canEditTask(task) ? (
        <button onClick={onComplete} title="Mark complete"
          className="mt-0.5 shrink-0 rounded border border-[var(--border)] p-0.5 hover:bg-green-50 hover:border-green-400">
          <CheckSquare size={14} className="text-gray-300 hover:text-green-500" />
        </button>
      ) : (
        <span className="mt-0.5 shrink-0 rounded border border-[var(--border)] p-0.5 opacity-40"
          title="Only the assignee, creator, or an admin can complete this task">
          <CheckSquare size={14} className="text-gray-300" />
        </span>
      ))}
      {done && <CheckSquare size={14} className="mt-0.5 shrink-0 text-green-500" />}
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${done ? 'line-through text-[var(--text-secondary)]' : 'text-[var(--text-primary)]'}`}>{task.title}</p>
        {task.body && <p className="mt-0.5 text-xs text-[var(--text-secondary)] truncate">{task.body}</p>}
        <div className="mt-1 flex items-center gap-2 text-[11px]">
          {task.due_at && (
            <span className={`flex items-center gap-1 ${overdue ? 'font-semibold text-red-600' : 'text-[var(--text-secondary)]'}`}>
              <Clock size={10} />{dueLabel(task.due_at)}
              {overdue && ' · overdue'}
            </span>
          )}
          {task.owner_email && <span className="text-[var(--text-secondary)]">→ {task.owner_email.split('@')[0]}</span>}
        </div>
      </div>
    </div>
  );
}

function AddCompanyModal({ projectId, existingRoles, onClose, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ account_id: '', role: 'general_contractor', contact_id: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = 'mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';

  useEffect(() => {
    Promise.all([api.get('/crm/accounts'), api.get('/crm/contacts')]).then(([a, c]) => {
      setAccounts(a.accounts || []);
      setContacts(c.contacts || []);
    }).catch((e) => setErr(e.message));
  }, []);

  const filteredContacts = contacts.filter((c) => c.account_id === form.account_id);

  async function submit(e) {
    e.preventDefault();
    if (!form.account_id) { setErr('Account required'); return; }
    if (!form.role) { setErr('Role required'); return; }
    setSaving(true);
    try {
      await api.post('/crm/project-companies', {
        project_id: projectId,
        account_id: form.account_id,
        role: form.role,
        contact_id: form.contact_id || null,
        notes: form.notes || null,
      });
      onSaved();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm space-y-3 rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900">
        <h3 className="flex items-center gap-2 font-semibold text-[var(--text-primary)]"><Building2 size={16} /> Add Company to Project</h3>
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Company *
          <select className={input} value={form.account_id} onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value, contact_id: '' }))}>
            <option value="">— select account —</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Role *
          <select className={input} value={form.role} onChange={set('role')}>
            {Object.entries(ROLE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Key contact
          <select className={input} value={form.contact_id} onChange={set('contact_id')}>
            <option value="">— none —</option>
            {filteredContacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.title ? ` · ${c.title}` : ''}</option>)}
          </select>
          {form.account_id && filteredContacts.length === 0 && (
            <p className="mt-1 text-[11px] text-[var(--text-secondary)]">No contacts linked to this account yet.</p>
          )}
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Notes
          <input className={input} value={form.notes} onChange={set('notes')} placeholder="Optional context…" />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Add company'}
          </button>
        </div>
      </form>
    </div>
  );
}

function AddTaskModal({ projectId, leadId, accountId, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', body: '', due_at: '', assignee_user_id: '' });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = 'mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';

  // Org user directory for the assignee picker; empty list just leaves "Unassigned".
  useEffect(() => {
    api.get('/crm/users').then((r) => setUsers(r.users || [])).catch((e) => setErr(e.message));
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setErr('Title required'); return; }
    setSaving(true);
    try {
      await api.post('/crm/tasks', {
        title: form.title.trim(),
        body: form.body || null,
        due_at: form.due_at || null,
        assignee_user_id: form.assignee_user_id || null,
        project_id: projectId || null,
        lead_id: leadId || null,
        account_id: accountId || null,
      });
      onSaved();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm space-y-3 rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900">
        <h3 className="font-semibold text-[var(--text-primary)]">Add Task</h3>
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Title *<input className={input} value={form.title} onChange={set('title')} /></label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Notes<textarea rows={2} className={input} value={form.body} onChange={set('body')} /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Due date<input type="date" className={input} value={form.due_at} onChange={set('due_at')} /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Assign to
            <select className={input} value={form.assignee_user_id} onChange={set('assignee_user_id')}>
              <option value="">— Me (default) —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.display_name || u.email}</option>)}
            </select>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Add task'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { AddTaskModal };
